/* Flow props */
import type { SdkConfigPayload } from 'flow/editor/components/MenuBar/MenuBarModals/ThreeDotsModals/SDKConfigModal/types';
import type { AggregationComponent, ReasonCode } from '~/dashboard/views/workflows/store/engine/types';
import type { FraudAttackRadarConfig } from '~/fraud-attack-radar/types';

export type DropdownOption = {
  label: string;
  value: string;
};

export type DependentDropdownOptions = {
  [key: string]: DropdownOption[];
};

export interface Attribute {
  archivedAt: string | null;
  attribute_name: string;
  attribute_id?: number;
  attribute_token?: string;
  components?: Array<AttributeComponent>;
  display_name?: string;
  dataType: string;
  isMultilevel: boolean;
  // BE endpoint response returns both
  is_multilevel?: boolean;
  right_side_dropdown_options?: DropdownOption[] | DependentDropdownOptions;
  values?: string | null;
  default?: string | string[] | number | null;
}

export interface AttributeComponent {
  // The attributes here were derived from the React Dev tools
  action_threshold_attribute_direction?: string;
  action_threshold_id?: number;
  attribute_component_id: number;
  attribute_id: number;
  data_type: string;
  default?: string;
  is_optional: boolean;
  label: string;
  placeholder: string;
  possible_values: (string | Array<string>)[] | string;
  precedence: number;
  shouldApplyFilter?: boolean;
  value?: string | string[];

  // pre-existing stuff. not sure if still relevant.
  attribute_name?: string;
  dataType?: string;

  // These were derived from flowHelper. I guess it's recursive?
  is_mock_mla_filter?: any;
  attribute_component?: AttributeComponent;
}

export interface Category {
  category_id: number;
  name: string;
}
export interface List {
  customer_list_id: number;
  list_name: string;
}

export interface OutcomeReason {
  name: string;
}

export interface ReasonCodeGroup {
  archived: boolean;
  id: number;
  name: string;
  services?: Array<string>;
  reasonCodes: Array<number | ReasonCode>;
}

export interface RecurringWorkflows {
  [key: string]: Array<{
    application_id: number;
    name: string;
    service_name: string;
  }>;
}

export interface ServiceObject {
  attributes: {
    [attribute_id: number]: Attribute;
  };
  categories: {
    request: Array<Category>;
    response: Array<Category>;
  };
  enabled: boolean;
  id: number;
  internal_name: string;
  name: string;
  description?: string;
  production_enabled: boolean;
  sandbox_enabled: boolean;
  service_id: number;
}

export interface Services {
  [service_id: number]: ServiceObject;
}

export interface ServicesByName {
  [internal_name: string]: ServiceObject;
}


export interface Tag {
  customer_tag_id: number;
  customer_id: number;
  tag_name: string;
  tag_color: string;
  category_id: number | null;
  archived: boolean;
  is_case_creating: boolean;
  suppression_interval: object;
}

export interface TagType {
  name: string;
  tags: Array<Tag>;
}

export interface Workflow {
  application_id: number;
  application_token: string;
  archived?: boolean;
  champion_challenger?: string;
  champion_challenger_started: string | null;
  champion_challenger_ended: string | null;
  id?: string;
  is_backtest: boolean;
  most_recent_version_timestamp?: string;
  name: string;
  type: string;
  use_case?: string | null;
  use_case_display_name?: string | null;
  entity_type?: string | null;
  url?: string;
  version_count?: number;
  fraud_attack_radar_config?: FraudAttackRadarConfig;
  fraud_attack_radar_running_at?: string | null;
  fraud_attack_radar_risk_score?: string | null;
  fraud_attack_radar_eligible?: boolean | null;
  workflow_taxonomy_type_id?: number | null;
}

export interface WorkflowCheckout {
  checked_in_at: string | null;
  checked_out: boolean;
  checked_out_at: string | null;
  checkin_agent: string | null;
  checkout_agent: string | null;
  workflow_token: string;
}

export interface WorkflowVersion {
  active?: boolean;
  active_public_version?: number;
  application_id: number;
  application_name?: string;
  application_token: string;
  application_version_id: number;
  archived?: boolean;
  formatted_timestamp?: string;
  notes: string | null;
  outcomes: Outcome[];
  public_version?: number;
  services?: string;
  services_list?: string[];
  timestamp?: string;
  use_best_effort_threshold_rule_parser?: boolean;
}

export interface WorkflowActiveVersion extends Workflow {
  timestamp: string;
  changes: string | null;
  parent_application_version_id: number | null;
  agent_id: number | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  parent_public_version: number | null;
  services: string | null;
  outcomes: {
    className: string | null;
    outcome: string | null;
    tooltip: string | null;
  }[] | null;
  tags: Tag[] | null;
  active: boolean;
  active_checkbox: {
    href: string;
    method: string;
    checked: boolean;
    property: string;
    value: number;
    disabled: boolean | null;
    extra: string[];
    confirm: string;
  } | null;
  alerts: object;
  alerts_fa: object;
  view_link: {
    href: string;
    text: string;
  } | null;
  parent_version_link: string | null;
  data_url: string;
  public_version?: number;
  formatted_timestamp?: string;
  application_id: number;
  application_token: string;
  application_version_id: number;
  notes: string | null;
  services_list: string[];
  percentage?: number | null;
  color?: string;
}

/* Flow state */
export interface Action {
  id: number;
  actionId: number;
  serviceId: number | null;
  type: 'run' | 'tag' | 'outcome' | 'input' | 'output' | 'imported_output_attribute' | 'imported_matrix_model' | 'outcome_reason';
  name: string;
  booleanRule: string;
  customerTagId: number | null;
  tagColor: string | null;
  customerOutcomeId: number | null;
  applicationId: number | null;
  precedence: number | null;
  serviceFailureDefault: boolean | null;
  options: {
    [key: string]: ActionOption;
  };
  thresholdsRule: string;
  publishedAttributeToken?: string;
}

export interface ActionOption {
  actionId: number;
  id: number;
  jsonValue: any;
  key: string;
  publishedAttributeToken: any;
  value: string;
}

export interface DataRoute {
  id?: number;
  db_id?: number;
  sourceId?: number | false;
  targetId?: number;
  category?: {
    id: number;
    name: string;
  };
  attribute?: DataRouteAttribute;
  aggregationComponents?: AggregationComponent[];
}

export interface DataRouteAttribute {
  id: number;
  name: string;
  attribute_name: string;
  display_name: string;
  is_multilevel: boolean;
  multi_level_attribute_filter: {
    filter_components: any;
  };
}

export interface DataRouteCategory {
  request?: string;
  response?: string;
}

export type MatrixModelAxis = 'x' | 'y';

export interface DataRouteMatrixModelAxis {
  [actionId: number]: {
    [axis: string]: {
      attribute?: DataRouteAttribute;
      id: number;
      sourceId?: number;
      source?: {
        name: string;
        type: string;
      };
    };
  };
  update?: MatrixModelAxis | false;
}

export interface MatrixModel {
  actionId: number;
  name: string;
  matrix_data_type: string;
  matrix: Array<Array<string>>;
  x_axis_header: Array<number>;
  y_axis_header: Array<number>;
  x_axis_header_data_type: string;
  y_axis_header_data_type: string;
}

export interface Relationship {
  sourceId: number;
  targetId: number;
}

export type BooleanString = 'true' | 'false';
export type OperatorString =
  // Equals
  'e' |
  // Not equals
  'ne' |
  // Less than
  'lt' |
  // Less then or equal to
  'le' |
  // Greater than
  'gt' |
  // Greater than or equal to
  'ge' |
  // In the range of
  'range' |
  // In the evaluated object
  'in' |
  // Not in the evaluated object
  'nin' |
  // Contains the evaluated object
  'con' |
  // Does not contain the evaluated object
  'ncon';

export interface Threshold {
  id: number;
  actionId?: number;
  actId?: number;
  leftActionId?: number;
  leftAttrId?: number | false | null;
  leftAttrComponents?: Array<AttributeComponent>;
  leftAggregationComponents?: Array<AggregationComponent>;
  leftCategoryId?: number | false | null;
  operator: OperatorString;
  rightActionId?: number | false | null;
  rightAttrId?: number | false | null;
  rightAttrComponents?: Array<AttributeComponent>;
  rightCategoryId?: number | false | null;
  rightCRGId?: number | false | null;
  rightTagActionSet?: any;
  rightAttrValue: string | false | null;
  rightAggregationComponents?: Array<AggregationComponent>;
}

export interface Outcome {
  archived: boolean;
  customer_outcome_id: number;
  id: number;
  is_event_status: boolean;
  outcome: string;
}

export interface OutcomeReasonWorkflowsByTagId {
  [tag_id: number]: {
    name: string;
    token: string;
    version: number;
    tag_id: number;
    archived: boolean;
  }[];
}

export interface FlowState {
  actions: Array<Action>;
  actionOptions: Array<ActionOption>;
  dataRoutes: Array<DataRoute>;
  dataRouteMatrixModelAxis: {
    [actionId: number]: {};
    update?: MatrixModelAxis | false;
  };
  matrixModels: Array<MatrixModel>;
  outcomes: Array<Outcome>;
  relationships: Array<Relationship>;
  tags: Array<Tag>;
  thresholds: Array<Threshold>;
  sdkConfig: SdkConfigPayload;
  workflowsOutcomeReasonTagsByTagId: OutcomeReasonWorkflowsByTagId;
  version: WorkflowVersion;
  failureHandlingMode?: 'best-effort' | 'full-threshold';
}


export enum WorkflowScheduleIntervals {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  ANNUALLY = 'annually',
}

export enum WorkflowScheduleDates {
  FIRST = '1', // First day of the month
  FIFTEENTH = '15',
  LAST = 'L', // Last day of the month
}

export enum WorkflowScheduleWeekdays {
  SUN = 'SUN',
  MON = 'MON',
  TUE = 'TUE',
  WED = 'WED',
  THU = 'THU',
  FRI = 'FRI',
  SAT = 'SAT',
}

/*
Template Literal Types
https://www.typescriptlang.org/docs/handbook/2/template-literal-types.html
*/
export type WorkflowSchedule = {
  interval: `${WorkflowScheduleIntervals}`;
  config: WorkflowScheduleConfig;
  isSandbox: boolean;
};

export type WorkflowScheduleConfig = {
  time: string; // 'HH:mm'

  as_of_clock_time?: string; // 'HH:mm'
  date?: `${WorkflowScheduleDates}`; // Monthly interval only
  weekDay?: string; // Weekly interval only
  month?: number; // Annual interval only
  day?: number; // Annual interval only
  startYear?: number; // Annual interval only
};

export type PublishedAttribute = {
  archived: boolean;
  contains_pii: boolean;
  data_type: string;
  default_value: any;
  default_value_note?: string;
  description?: string;
  jq_filter?: string;
  object_type: string;
  published_attribute_name: string;
  published_attribute_token: string;
  value_expires_in: string;
};
